import type {
  EffectMetamodelManifestSpec,
  DomainEffectBehaviorsSummary,
  DomainMethodCreationSummary,
  DomainMethodIR,
  DomainMethodSummary,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function normalizeEffectBehaviorSummary(
  metamodels?: Record<string, unknown> | null,
): DomainEffectBehaviorsSummary | null {
  if (!isObject(metamodels)) {
    return null;
  }

  const result: DomainEffectBehaviorsSummary = {};

  const rawPostCondition = metamodels.postCondition;
  if (
    typeof rawPostCondition === "string" &&
    rawPostCondition.trim().length > 0
  ) {
    result.postCondition = { condition: rawPostCondition.trim() };
  } else if (
    isObject(rawPostCondition) &&
    typeof rawPostCondition.condition === "string" &&
    rawPostCondition.condition.trim().length > 0
  ) {
    result.postCondition = {
      condition: rawPostCondition.condition.trim(),
      ...(typeof rawPostCondition.description === "string"
        ? { description: rawPostCondition.description }
        : {}),
    };
  }

  const rawDryRun = metamodels.dryRun;
  if (typeof rawDryRun === "boolean") {
    result.dryRun = { enabled: rawDryRun };
  } else if (isObject(rawDryRun)) {
    result.dryRun = {
      enabled: rawDryRun.enabled !== false,
      ...(typeof rawDryRun.description === "string"
        ? { description: rawDryRun.description }
        : {}),
    };
  }

  const rawReverse = metamodels.reverse;
  if (typeof rawReverse === "string" && rawReverse.trim().length > 0) {
    result.reverse = { handler: rawReverse.trim() };
  } else if (isObject(rawReverse)) {
    result.reverse = {
      ...(typeof rawReverse.handler === "string" &&
      rawReverse.handler.trim().length > 0
        ? { handler: rawReverse.handler.trim() }
        : {}),
      ...(typeof rawReverse.description === "string"
        ? { description: rawReverse.description }
        : {}),
    };
  }

  const rawApprovalRequired = metamodels.approvalRequired;
  if (typeof rawApprovalRequired === "boolean") {
    result.approvalRequired = { required: rawApprovalRequired };
  } else if (isObject(rawApprovalRequired)) {
    result.approvalRequired = {
      required: rawApprovalRequired.required !== false,
      ...(typeof rawApprovalRequired.reason === "string"
        ? { reason: rawApprovalRequired.reason }
        : {}),
      ...(typeof rawApprovalRequired.mode === "string"
        ? { mode: rawApprovalRequired.mode }
        : {}),
    };
  }

  return Object.keys(result).length > 0 ? result : null;
}

export function normalizeCreationSummary(
  metamodels?: Record<string, unknown> | null,
): DomainMethodCreationSummary | null {
  if (!isObject(metamodels)) return null;
  let raw = metamodels.creates;
  if (typeof raw === "string" && raw.trim().length > 0) {
    const trimmed = raw.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith('"')) {
      try {
        raw = JSON.parse(trimmed);
      } catch {
        return { className: trimmed };
      }
    } else {
      return { className: trimmed };
    }
  }
  if (typeof raw === "string" && raw.trim().length > 0) {
    return { className: raw.trim() };
  }
  if (!isObject(raw)) return null;
  const className =
    typeof raw.className === "string" && raw.className.trim()
      ? raw.className.trim()
      : "";
  if (!className) return null;
  return {
    className,
    ...(typeof raw.idPath === "string" && raw.idPath.trim()
      ? { idPath: raw.idPath.trim() }
      : {}),
    ...(typeof raw.pathPath === "string" && raw.pathPath.trim()
      ? { pathPath: raw.pathPath.trim() }
      : {}),
    ...(typeof raw.statePath === "string" && raw.statePath.trim()
      ? { statePath: raw.statePath.trim() }
      : {}),
    ...(typeof raw.classStateHandle === "boolean"
      ? { classStateHandle: raw.classStateHandle }
      : {}),
  };
}

export function buildEffectBehaviorDocs(
  effectBehaviors?: DomainEffectBehaviorsSummary | null,
  creates?: DomainMethodCreationSummary | null,
): string[] {
  const docs: string[] = [];

  if (creates) {
    docs.push(
      `Creation method: creates ${creates.className}. The agent may use generated class-level new-record action methods for this class.`,
    );
  }

  if (effectBehaviors?.approvalRequired?.required) {
    docs.push(
      effectBehaviors.approvalRequired.reason
        ? `Approval required: ${effectBehaviors.approvalRequired.reason}.`
        : "Approval required before execution.",
    );
  }

  if (effectBehaviors?.postCondition) {
    docs.push(`Post-condition: ${effectBehaviors.postCondition.condition}.`);
    if (effectBehaviors.postCondition.description) {
      docs.push(effectBehaviors.postCondition.description);
    }
  }

  if (effectBehaviors?.dryRun?.enabled) {
    docs.push("Supports dry run.");
    if (effectBehaviors.dryRun.description) {
      docs.push(effectBehaviors.dryRun.description);
    }
  }

  if (effectBehaviors?.reverse) {
    if (effectBehaviors.reverse.handler) {
      docs.push(`Reverse handler: ${effectBehaviors.reverse.handler}.`);
    } else {
      docs.push("Supports reverse execution.");
    }
    if (effectBehaviors.reverse.description) {
      docs.push(effectBehaviors.reverse.description);
    }
  }

  return docs;
}

export function buildEffectBehaviorMutations(
  toolPath: string,
  spec?: EffectMetamodelManifestSpec,
) {
  if (!spec) return [];

  const mutations: Array<{ label: string; query: string }> = [];

  if (spec.postCondition) {
    const config =
      typeof spec.postCondition === "string"
        ? { condition: spec.postCondition }
        : spec.postCondition;
    const descriptionArg = config.description
      ? `, description: ${JSON.stringify(config.description)}`
      : "";
    mutations.push({
      label: `set postCondition on ${toolPath}`,
      query: `mutation { at(path: ${JSON.stringify(toolPath)}) { set_post_condition(condition: ${JSON.stringify(
        config.condition,
      )}${descriptionArg}) { kind } } }`,
    });
  }

  if (spec.dryRun !== undefined) {
    const config =
      typeof spec.dryRun === "boolean" ? { enabled: spec.dryRun } : spec.dryRun;
    const args = [
      config.enabled !== undefined ? `enabled: ${config.enabled}` : null,
      config.description
        ? `description: ${JSON.stringify(config.description)}`
        : null,
    ]
      .filter(Boolean)
      .join(", ");
    mutations.push({
      label: `set dryRun on ${toolPath}`,
      query: `mutation { at(path: ${JSON.stringify(toolPath)}) { set_dry_run(${args}) { kind } } }`,
    });
  }

  if (spec.reverse !== undefined) {
    const config =
      typeof spec.reverse === "string"
        ? { handler: spec.reverse }
        : spec.reverse;
    const args = [
      config.handler ? `handler: ${JSON.stringify(config.handler)}` : null,
      config.description
        ? `description: ${JSON.stringify(config.description)}`
        : null,
    ]
      .filter(Boolean)
      .join(", ");
    mutations.push({
      label: `set reverse on ${toolPath}`,
      query: `mutation { at(path: ${JSON.stringify(toolPath)}) { set_reverse(${args}) { kind } } }`,
    });
  }

  if (spec.approvalRequired !== undefined) {
    const config =
      typeof spec.approvalRequired === "boolean"
        ? { required: spec.approvalRequired }
        : spec.approvalRequired;
    const args = [
      config.required !== undefined ? `required: ${config.required}` : null,
      config.reason ? `reason: ${JSON.stringify(config.reason)}` : null,
      config.mode ? `mode: ${JSON.stringify(config.mode)}` : null,
    ]
      .filter(Boolean)
      .join(", ");
    mutations.push({
      label: `set approvalRequired on ${toolPath}`,
      query: `mutation { at(path: ${JSON.stringify(toolPath)}) { set_approval_required(${args}) { kind } } }`,
    });
  }

  if (spec.creates !== undefined) {
    mutations.push({
      label: `set creates on ${toolPath}`,
      query: `mutation { at(path: ${JSON.stringify(toolPath)}) { create_submodel(subpath: "creates", label: "creates") { set_string_value(value: ${JSON.stringify(
        JSON.stringify(spec.creates),
      )}) { done } } } }`,
    });
  }

  return mutations;
}

export function readMethodEffectBehaviors(
  rawMethod: Record<string, any>,
): Partial<DomainMethodSummary> {
  const metamodels = isObject(rawMethod.metamodels)
    ? rawMethod.metamodels
    : null;
  return {
    effectBehaviors: normalizeEffectBehaviorSummary(metamodels),
    creates: normalizeCreationSummary(metamodels),
  };
}

export const effectBehaviorsMetamodelPackage = defineMetamodelPackage({
  id: "effect_behaviors",
  docs: {
    effectRows: [
      {
        key: "postCondition",
        description: "String condition or `{ condition, description }`.",
      },
      {
        key: "dryRun",
        description: "Boolean or `{ enabled, description }`.",
      },
      {
        key: "reverse",
        description: "Handler name string or `{ handler, description }`.",
      },
      {
        key: "approvalRequired",
        description: "Boolean or `{ required, reason, mode }`.",
      },
      {
        key: "creates",
        description:
          'Marks a static method as an allowed creator for a class. Use `creates: "class_name"` or `{ className, idPath, pathPath, statePath, classStateHandle }`.',
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type EffectBehaviorMetamodel {
        model: Model!
        kind: String!
        condition: String
        description: String
        handler: String
        reason: String
        mode: String
        required: Boolean
        enabled: Boolean
      }

      extend type Model {
        effect_behaviors: [EffectBehaviorMetamodel!]!
      }

      extend type ModelMutation {
        set_post_condition(condition: String!, description: String): EffectBehaviorMetamodel
        set_dry_run(enabled: Boolean, description: String): EffectBehaviorMetamodel
        set_reverse(handler: String, description: String): EffectBehaviorMetamodel
        set_approval_required(required: Boolean, reason: String, mode: String): EffectBehaviorMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        EffectBehaviorMetamodel: {
          model: (value) => value.model,
          kind: (value) => value.kind,
          condition: (value) => value.condition || null,
          description: (value) => value.description || null,
          handler: (value) => value.handler || null,
          reason: (value) => value.reason || null,
          mode: (value) => value.mode || null,
          required: (value) => value.required ?? null,
          enabled: (value) => value.enabled ?? null,
        },
        Model: {
          effect_behaviors: async (ant) => await run(ant.effect_behaviors()),
        },
        ModelMutation: {
          set_post_condition: async (ant, { condition, description }) => {
            const model = await run(
              ant.set_effect_behavior({
                kind: "post_condition",
                condition,
                description,
              }),
            );
            return { model, kind: "post_condition", condition, description };
          },
          set_dry_run: async (ant, { enabled, description }) => {
            const finalEnabled = enabled ?? true;
            const model = await run(
              ant.set_effect_behavior({
                kind: "dry_run",
                enabled: finalEnabled,
                description,
              }),
            );
            return {
              model,
              kind: "dry_run",
              enabled: finalEnabled,
              description,
            };
          },
          set_reverse: async (ant, { handler, description }) => {
            const model = await run(
              ant.set_effect_behavior({
                kind: "reverse",
                handler,
                description,
              }),
            );
            return { model, kind: "reverse", handler, description };
          },
          set_approval_required: async (ant, { required, reason, mode }) => {
            const finalRequired = required ?? true;
            const model = await run(
              ant.set_effect_behavior({
                kind: "approval_required",
                required: finalRequired,
                reason,
                mode,
              }),
            );
            return {
              model,
              kind: "approval_required",
              required: finalRequired,
              reason,
              mode,
            };
          },
        },
      };
    },
  },
  manifest: {
    buildEffectMutations(toolPath, spec) {
      return buildEffectBehaviorMutations(toolPath, spec);
    },
  },
  summary: {
    readMethodSummary: readMethodEffectBehaviors,
  },
  domain: {
    applyToMethodIR(
      methodIR: DomainMethodIR,
      methodSummary: DomainMethodSummary,
    ) {
      return {
        ...methodIR,
        docs: [
          ...methodIR.docs,
          ...buildEffectBehaviorDocs(
            methodSummary.effectBehaviors,
            methodSummary.creates,
          ),
        ],
      };
    },
  },
});
